import React, { useMemo, useEffect } from 'react';
import Box from '../../atoms/Box';
import PropTypes from 'prop-types';
import * as BS from 'react-bootstrap';
import * as BSEx from '../../BootstrapExtends';
import {
  SelectableFilterColumns,
  FilterComparisonOperators,
} from '../../../enums/SpreadsheetReportConfigs';
import {
  DEFAULT_FILTER_CONDITION,
  AGGREGATION_AXIS_COLUMN_GROUP_KEYS,
  getAggregationAxisColumnGroupKey,
  getSelectedAggregationAxisGroupKeys,
  getConflictingAggregationAxisGroupKeys,
  AGGREGATION_AXIS_COLUMN_VALUES,
  ADVERTISING_TITLE_COLUMN_VALUES,
} from '../../../constants/SpreadsheetReportConfigs.js';
import ConversionPoint from '../../../models/SpreadsheetReportConfigs/ConversionPoint';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Form.CustomReportForm.sheetConfigSection.panelSection.outputDataFilterSection';

const OutputDataFilter = ({
  filters = [[DEFAULT_FILTER_CONDITION]],
  conversionPoints,
  selectedColumns = [],
  errors = {},
  touched = {},
  onChange,
  onBlur, // Add onBlur prop
  channelOptions = [],
}) => {
  const filterColumnOptions = useMemo(() => {
    const defaultOptions = Object.entries(SelectableFilterColumns).map(
      ([_, option]) => ({
        value: option.value,
        label: option.label,
      })
    );
    if (conversionPoints.length > 0) {
      return [...defaultOptions, ...conversionPoints];
    }
    return defaultOptions;
  }, [conversionPoints]);

  useEffect(() => {
    let hasChanges = false;
    const normalizedFilters = filters.map((andGroup) =>
      andGroup.map((condition) => {
        if (
          condition.targetColumn ===
            AGGREGATION_AXIS_COLUMN_VALUES.prefecture &&
          condition.value === 'Unknown'
        ) {
          hasChanges = true;
          return { ...condition, value: 'Other' };
        }
        return condition;
      })
    );
    if (hasChanges) {
      onChange(normalizedFilters);
    }
  }, [filters, onChange]);

  const selectedAggregationAxisGroupKeys = useMemo(() => {
    return getSelectedAggregationAxisGroupKeys({
      columns: selectedColumns,
      filters,
    });
  }, [filters, selectedColumns]);

  const conflictingAggregationAxisGroupKeys = useMemo(() => {
    return getConflictingAggregationAxisGroupKeys(
      selectedAggregationAxisGroupKeys
    );
  }, [selectedAggregationAxisGroupKeys]);

  const handleAddAndGroup = () => {
    onChange([...filters, [DEFAULT_FILTER_CONDITION]]); // New group starts with one condition
  };

  const metricColumnSet = useMemo(() => {
    const metricValues = new Set(
      Object.values(ADVERTISING_TITLE_COLUMN_VALUES)
    );
    conversionPoints.forEach((point) => {
      if (point?.value) metricValues.add(point.value);
    });
    return metricValues;
  }, [conversionPoints]);

  const dropdownFilterColumnSet = useMemo(
    () =>
      new Set([
        AGGREGATION_AXIS_COLUMN_VALUES.channel,
        AGGREGATION_AXIS_COLUMN_VALUES.gender,
        AGGREGATION_AXIS_COLUMN_VALUES.prefecture,
      ]),
    []
  );

  const handleAddCondition = (andGroupIndex) => {
    const newFilters = [...filters];
    newFilters[andGroupIndex] = [
      ...newFilters[andGroupIndex],
      { ...DEFAULT_FILTER_CONDITION },
    ];
    onChange(newFilters);
  };

  const handleRemoveCondition = (andGroupIndex, conditionIndex) => {
    const currentGroup = filters[andGroupIndex];
    if (currentGroup.length === 1) {
      const newFilters = filters.filter((_, index) => index !== andGroupIndex);
      onChange(newFilters);
    } else {
      const newFilters = [...filters];
      newFilters[andGroupIndex] = currentGroup.filter(
        (_, index) => index !== conditionIndex
      );
      onChange(newFilters);
    }
  };

  const handleConditionChange = (
    andGroupIndex,
    conditionIndex,
    field,
    value
  ) => {
    const newFilters = [...filters];

    const currentCondition = { ...newFilters[andGroupIndex][conditionIndex] };

    const getAllowedOperators = (targetColumn) => {
      if (dropdownFilterColumnSet.has(targetColumn)) {
        return [
          FilterComparisonOperators.equals.value,
          FilterComparisonOperators.notEquals.value,
        ];
      }
      if (metricColumnSet.has(targetColumn)) {
        return [
          FilterComparisonOperators.equals.value,
          FilterComparisonOperators.notEquals.value,
          FilterComparisonOperators.greaterThan.value,
          FilterComparisonOperators.lessThan.value,
          FilterComparisonOperators.greaterThanOrEqual.value,
          FilterComparisonOperators.lessThanOrEqual.value,
        ];
      }
      return [
        FilterComparisonOperators.equals.value,
        FilterComparisonOperators.notEquals.value,
        FilterComparisonOperators.contains.value,
        FilterComparisonOperators.notContains.value,
        FilterComparisonOperators.startsWith.value,
        FilterComparisonOperators.endsWith.value,
      ];
    };

    const normalizeConditionOperator = (targetColumn, operatorValue) => {
      const allowed = getAllowedOperators(targetColumn);
      return allowed.includes(operatorValue) ? operatorValue : allowed[0];
    };

    if (field === 'targetColumn') {
      const nextTargetColumn = value;
      const nextCondition = normalizeConditionOperator(
        nextTargetColumn,
        currentCondition.condition
      );
      newFilters[andGroupIndex][conditionIndex] = {
        ...currentCondition,
        targetColumn: nextTargetColumn,
        condition: nextCondition,
        value: '',
      };
    } else if (field === 'condition') {
      const nextCondition = normalizeConditionOperator(
        currentCondition.targetColumn,
        value
      );
      newFilters[andGroupIndex][conditionIndex] = {
        ...currentCondition,
        condition: nextCondition,
      };
    } else {
      newFilters[andGroupIndex][conditionIndex] = {
        ...currentCondition,
        [field]: value,
      };
    }
    onChange(newFilters);
  };

  const handleFieldBlur = (andGroupIndex, conditionIndex, field) => {
    if (onBlur) {
      onBlur(`filters[${andGroupIndex}][${conditionIndex}].${field}`);
    }
  };

  const getTouched = (andGroupIndex, conditionIndex, field) => {
    return touched?.filters?.[andGroupIndex]?.[conditionIndex]?.[field];
  };

  const getError = (andGroupIndex, conditionIndex, field) => {
    return errors?.filters?.[andGroupIndex]?.[conditionIndex]?.[field];
  };

  const conflictingAggregationAxisGroupKeySet = useMemo(
    () => new Set(conflictingAggregationAxisGroupKeys),
    [conflictingAggregationAxisGroupKeys]
  );

  const sortedChannelOptions = useMemo(() => {
    return channelOptions.map((option) => ({
      key: option.key || option.value,
      value: option.value,
      label: option.label,
    }));
  }, [channelOptions]);

  const prefectureOptions = useMemo(() => {
    const translations = I18n.t('prefecture');
    if (!translations || typeof translations !== 'object') return [];
    return Object.entries(translations)
      .filter(([value]) => value !== 'Unknown' && value !== 'Other')
      .map(([value, label]) => ({
        value,
        label,
      }));
  }, []);

  const genderOptions = useMemo(() => {
    const translations = I18n.t('js.projects.gender');
    if (!translations || typeof translations !== 'object') return [];
    const allowedKeys = ['female', 'male', 'unknown'];
    return allowedKeys
      .filter((key) => translations[key])
      .map((key) => ({
        value: key === 'unknown' ? 'unknown_group' : key,
        label: translations[key],
      }));
  }, []);

  const isAggregationAxisOptionDisabled = (
    optionValue,
    currentTargetColumn
  ) => {
    if (conflictingAggregationAxisGroupKeySet.size === 0) return false;

    const optionGroupKey = getAggregationAxisColumnGroupKey(optionValue);

    if (
      !optionGroupKey ||
      optionGroupKey === AGGREGATION_AXIS_COLUMN_GROUP_KEYS.always ||
      optionValue === currentTargetColumn
    ) {
      return false;
    }

    return conflictingAggregationAxisGroupKeySet.has(optionGroupKey);
  };

  return (
    <>
      <h4>{I18n.t(`${scope}.sectionTitle`)}</h4>

      {filters.map((andGroup, andGroupIndex) => (
        <Box key={andGroupIndex}>
          <Box.Body>
            {andGroup.map((condition, conditionIndex) => (
              <BS.Row key={conditionIndex}>
                <BS.Col sm={4}>
                  <BSEx.FormGroupInline
                    error={getError(
                      andGroupIndex,
                      conditionIndex,
                      'targetColumn'
                    )}
                    touched={getTouched(
                      andGroupIndex,
                      conditionIndex,
                      'targetColumn'
                    )}
                  >
                    <BS.FormControl
                      componentClass='select'
                      value={condition.targetColumn}
                      onChange={(e) =>
                        handleConditionChange(
                          andGroupIndex,
                          conditionIndex,
                          'targetColumn',
                          e.target.value
                        )
                      }
                      onBlur={() =>
                        handleFieldBlur(
                          andGroupIndex,
                          conditionIndex,
                          'targetColumn'
                        )
                      }
                    >
                      {filterColumnOptions.map((option) => {
                        const isDisabledOption =
                          isAggregationAxisOptionDisabled(
                            option.value,
                            condition.targetColumn
                          );

                        return (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={isDisabledOption}
                            style={
                              isDisabledOption
                                ? { backgroundColor: '#f5f5f5', color: '#888' }
                                : undefined
                            }
                            title={
                              isDisabledOption
                                ? '選択済みの項目と同時に利用できません'
                                : undefined
                            }
                          >
                            {option.label}
                          </option>
                        );
                      })}
                    </BS.FormControl>
                  </BSEx.FormGroupInline>
                </BS.Col>

                <BS.Col sm={3}>
                  <BSEx.FormGroupInline
                    error={getError(andGroupIndex, conditionIndex, 'condition')}
                    touched={getTouched(
                      andGroupIndex,
                      conditionIndex,
                      'condition'
                    )}
                  >
                    {(() => {
                      const allowedOperators = ((targetColumn) => {
                        if (dropdownFilterColumnSet.has(targetColumn)) {
                          return [
                            FilterComparisonOperators.equals,
                            FilterComparisonOperators.notEquals,
                          ];
                        }
                        if (metricColumnSet.has(targetColumn)) {
                          return [
                            FilterComparisonOperators.equals,
                            FilterComparisonOperators.notEquals,
                            FilterComparisonOperators.greaterThan,
                            FilterComparisonOperators.lessThan,
                            FilterComparisonOperators.greaterThanOrEqual,
                            FilterComparisonOperators.lessThanOrEqual,
                          ];
                        }
                        return [
                          FilterComparisonOperators.equals,
                          FilterComparisonOperators.notEquals,
                          FilterComparisonOperators.contains,
                          FilterComparisonOperators.notContains,
                          FilterComparisonOperators.startsWith,
                          FilterComparisonOperators.endsWith,
                        ];
                      })(condition.targetColumn);

                      const operatorValue = allowedOperators.some(
                        (op) => op.value === condition.condition
                      )
                        ? condition.condition
                        : allowedOperators[0].value;

                      return (
                        <BS.FormControl
                          componentClass='select'
                          value={operatorValue}
                          onChange={(e) =>
                            handleConditionChange(
                              andGroupIndex,
                              conditionIndex,
                              'condition',
                              e.target.value
                            )
                          }
                          onBlur={() =>
                            handleFieldBlur(
                              andGroupIndex,
                              conditionIndex,
                              'condition'
                            )
                          }
                        >
                          {allowedOperators.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </BS.FormControl>
                      );
                    })()}
                  </BSEx.FormGroupInline>
                </BS.Col>

                <BS.Col sm={4}>
                  <BSEx.FormGroupInline
                    error={getError(andGroupIndex, conditionIndex, 'value')}
                    touched={getTouched(andGroupIndex, conditionIndex, 'value')}
                  >
                    {(() => {
                      const isChannelFilter =
                        condition.targetColumn ===
                        AGGREGATION_AXIS_COLUMN_VALUES.channel;
                      const isGenderFilter =
                        condition.targetColumn ===
                          AGGREGATION_AXIS_COLUMN_VALUES.gender &&
                        genderOptions.length > 0;
                      const isPrefectureFilter =
                        condition.targetColumn ===
                          AGGREGATION_AXIS_COLUMN_VALUES.prefecture &&
                        prefectureOptions.length > 0;
                      const isMetricFilter = metricColumnSet.has(
                        condition.targetColumn
                      );
                      if (isChannelFilter) {
                        return (
                          <BS.FormControl
                            componentClass='select'
                            value={condition.value}
                            onChange={(e) =>
                              handleConditionChange(
                                andGroupIndex,
                                conditionIndex,
                                'value',
                                e.target.value
                              )
                            }
                            onBlur={() =>
                              handleFieldBlur(
                                andGroupIndex,
                                conditionIndex,
                                'value'
                              )
                            }
                          >
                            <option value=''>
                              {I18n.t('frontend.common.selection.blank')}
                            </option>
                            {sortedChannelOptions.map((option) => (
                              <option key={option.key} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </BS.FormControl>
                        );
                      }
                      if (isGenderFilter) {
                        return (
                          <BS.FormControl
                            componentClass='select'
                            value={condition.value}
                            onChange={(e) =>
                              handleConditionChange(
                                andGroupIndex,
                                conditionIndex,
                                'value',
                                e.target.value
                              )
                            }
                            onBlur={() =>
                              handleFieldBlur(
                                andGroupIndex,
                                conditionIndex,
                                'value'
                              )
                            }
                          >
                            <option value=''>
                              {I18n.t('frontend.common.selection.blank')}
                            </option>
                            {genderOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </BS.FormControl>
                        );
                      }
                      if (isPrefectureFilter) {
                        return (
                          <BS.FormControl
                            componentClass='select'
                            value={condition.value}
                            onChange={(e) =>
                              handleConditionChange(
                                andGroupIndex,
                                conditionIndex,
                                'value',
                                e.target.value
                              )
                            }
                            onBlur={() =>
                              handleFieldBlur(
                                andGroupIndex,
                                conditionIndex,
                                'value'
                              )
                            }
                          >
                            <option value=''>
                              {I18n.t('frontend.common.selection.blank')}
                            </option>
                            {prefectureOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </BS.FormControl>
                        );
                      }
                      return (
                        <BS.FormControl
                          type={isMetricFilter ? 'number' : 'text'}
                          inputMode={isMetricFilter ? 'decimal' : 'text'}
                          step='any'
                          value={condition.value}
                          onChange={(e) =>
                            handleConditionChange(
                              andGroupIndex,
                              conditionIndex,
                              'value',
                              e.target.value
                            )
                          }
                          onBlur={() =>
                            handleFieldBlur(
                              andGroupIndex,
                              conditionIndex,
                              'value'
                            )
                          }
                        />
                      );
                    })()}
                  </BSEx.FormGroupInline>
                </BS.Col>

                <BS.Col
                  sm={1}
                  className='control-label'
                  style={{ textAlign: 'left' }}
                >
                  <BS.Button
                    bsStyle='danger'
                    bsSize='xs'
                    onClick={() =>
                      handleRemoveCondition(andGroupIndex, conditionIndex)
                    }
                  >
                    <i className='fa fa-minus' />
                  </BS.Button>
                </BS.Col>
              </BS.Row>
            ))}

            <BS.Button
              bsStyle='default'
              bsSize='sm'
              onClick={() => handleAddCondition(andGroupIndex)}
            >
              {I18n.t(`${scope}.action.addCondition`)}
            </BS.Button>
          </Box.Body>
        </Box>
      ))}

      <BS.Button
        bsStyle='default'
        onClick={handleAddAndGroup}
        style={{ marginLeft: '10px' }}
      >
        {filters.length === 0
          ? I18n.t(`${scope}.action.addFirstGroup`)
          : I18n.t(`${scope}.action.addMoreGroup`)}
      </BS.Button>
    </>
  );
};

OutputDataFilter.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        targetColumn: PropTypes.string.isRequired,
        condition: PropTypes.string.isRequired,
        value: PropTypes.any.isRequired,
      })
    )
  ),
  conversionPoints: PropTypes.arrayOf(
    PropTypes.shape(ConversionPoint.propTypes)
  ),
  selectedColumns: PropTypes.array,
  errors: PropTypes.object,
  touched: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  channelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

export default OutputDataFilter;
